import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ChairRent,
  ChairRentReport,
  CommissionRule,
  SetChairRent,
  SetCommissionRule,
  StaffEarningsReport,
} from '@salonomy/types';
import { api, expectOk, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

/**
 * WS9 phase 4 — commission & earnings + chair-rent recording (brief 09 §4). TanStack Query over the
 * generated client, mirroring data/services.ts / data/policy.ts.
 *
 * HONESTY (ADR-0009 §Consequences): earnings/commission are COMPUTED by the API over RECOGNISED revenue
 * (real takings + voucher REDEMPTION revenue — NEVER voucher sales), so the UI DISPLAYS the server's
 * earnings rows and never recomputes commission, revenue, or net-per-chair locally. Commission rules and
 * chair rent are current-state upserts (re-setting the same key replaces the value), not append-only
 * ledgers. Chair rent is RECORDING + REPORTING only — invoicing renters is out of scope.
 *
 * RLS is renter-aware-floor-visible (like payments): a renter sees its OWN rule / chair rent / earnings
 * row; the owner/manager sees the floor and the net-position-per-chair report.
 */

/** The date-range a commission/earnings report is read over (mirrors the API's DateRangeQuery). */
export interface EarningsRange {
  from: string;
  to: string;
  staffId?: string;
  tz?: string;
}

// ── Commission rules (per-staff default % + optional per-category override) ───────────

export function useCommissionRules(staffId?: string) {
  return useQuery({
    queryKey: qk.commissionRules(staffId ?? null),
    queryFn: async (): Promise<CommissionRule[]> =>
      unwrap(
        await api.GET('/v1/commission/rules', {
          params: { query: { staffId } },
        }),
      ),
  });
}

/**
 * Set (upsert) a commission rule for a staff member. `category` null/omitted sets the default rate; a
 * category string creates/updates the per-service-category override. Re-setting the same (staff,
 * category) replaces the rate.
 */
export function useSetCommissionRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SetCommissionRule): Promise<CommissionRule> =>
      unwrap(await api.POST('/v1/commission/rules', { body: body as never })),
    onSuccess: () => invalidateEarnings(qc, 'commission-rules'),
  });
}

export function useDeleteCommissionRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> =>
      expectOk(await api.DELETE('/v1/commission/rules/{id}', { params: { path: { id } } })),
    onSuccess: () => invalidateEarnings(qc, 'commission-rules'),
  });
}

// ── Chair rent (flat weekly/monthly per renter — recording only) ──────────────────────

export function useChairRents() {
  return useQuery({
    queryKey: qk.chairRents,
    queryFn: async (): Promise<ChairRent[]> => unwrap(await api.GET('/v1/chair-rents')),
  });
}

/** Record (upsert) a flat chair rent for a renter. Re-setting the same renter replaces the figure. */
export function useSetChairRent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SetChairRent): Promise<ChairRent> =>
      unwrap(await api.POST('/v1/chair-rents', { body: body as never })),
    onSuccess: () => invalidateEarnings(qc, 'chair-rents'),
  });
}

// ── Reports (read the server's computed earnings/commission — never recompute) ────────

/**
 * Per-staff earnings over a date range: commission computed by each staff member's rules over recognised
 * revenue (never voucher sales) + recorded chair rent. The takings/KPI surfaces render these rows.
 */
export function useStaffEarningsReport(range: EarningsRange, enabled = true) {
  return useQuery({
    queryKey: qk.staffEarnings(range),
    enabled,
    queryFn: async (): Promise<StaffEarningsReport> =>
      unwrap(
        await api.GET('/v1/reports/staff-earnings', {
          params: {
            query: { from: range.from, to: range.to, staffId: range.staffId, tz: range.tz },
          },
        }),
      ),
  });
}

/**
 * The owner's net-position-per-chair report: recorded chair rent + retained margin per renter over a
 * date range (recording only, not an invoice). Manager-only on the API.
 */
export function useChairRentReport(range: EarningsRange, enabled = true) {
  return useQuery({
    queryKey: qk.chairRentReport(range),
    enabled,
    queryFn: async (): Promise<ChairRentReport> =>
      unwrap(
        await api.GET('/v1/reports/chair-rent', {
          params: {
            query: { from: range.from, to: range.to, staffId: range.staffId, tz: range.tz },
          },
        }),
      ),
  });
}

// ── Invalidation helper ───────────────────────────────────────────────────────────────

/** A rule or chair-rent change re-bases every earnings/chair-rent report figure. */
function invalidateEarnings(
  qc: ReturnType<typeof useQueryClient>,
  changed: 'commission-rules' | 'chair-rents',
): void {
  void qc.invalidateQueries({
    queryKey: changed === 'commission-rules' ? ['commission-rules'] : qk.chairRents,
  });
  void qc.invalidateQueries({ queryKey: ['staff-earnings'] });
  void qc.invalidateQueries({ queryKey: ['chair-rent-report'] });
}
